import React, { useState } from "react";
import { Alert } from "antd";
import ExcelAiFilesCard from "./ExcelAiFilesCard";
import ExcelAiRunsCard from "./ExcelAiRunsCard";
import ExcelAiRunDetailDrawer from "./ExcelAiRunDetailDrawer";
import { Toast } from "../../ui";
import ExcelAiService from "../../../services/excelAiService";

/**
 * Processamento por IA de uma integração Excel, exibido inline quando o usuário escolhe esse modo.
 *
 * Não é um ERP novo: é um modo de trabalho dentro da integração Excel que já existe. Manda os
 * arquivos como eles são, a IA descobre o formato, normaliza e grava nas mesmas tabelas em que o
 * modelo padrão grava — por isso o aviso de sobrescrita, que não é decorativo.
 *
 * Funciona também com a empresa ainda não salva: os arquivos ficam no navegador e só sobem depois
 * que o `onGarantirSalvo` cria a empresa. O usuário não precisa saber que existe essa ordem.
 */
const ExcelAiPanel = ({
  syncId,
  podeEditar = true,
  onGarantirSalvo,
  onRunFinalizado,
}) => {
  const [processando, setProcessando] = useState(false);
  const [sinalAtualizacao, setSinalAtualizacao] = useState(0);
  const [runSelecionado, setRunSelecionado] = useState(null);

  // Arquivos escolhidos antes de a empresa existir. Ficam só em memória até haver um id.
  const [pendentes, setPendentes] = useState([]);

  const avisarRejeitados = (rejeitados) => {
    if (!rejeitados?.length) return;
    Toast.warning(
      rejeitados.length === 1
        ? `${rejeitados[0].nomeOriginal}: ${rejeitados[0].motivo}`
        : `${rejeitados.length} arquivos não foram aceitos. Veja o detalhe na lista.`,
    );
  };

  /**
   * Salva a empresa se preciso, envia o que estava em memória e dispara o processamento.
   * Os três passos são um só do ponto de vista do usuário: ele clicou em "Processar".
   */
  const processar = async () => {
    try {
      setProcessando(true);

      let id = syncId;
      if (!id) {
        id = await onGarantirSalvo?.();
        // Falhou a validação do cadastro (nome, projeto...). O próprio save já disse o que falta.
        if (!id) return;
      }

      if (pendentes.length > 0) {
        const resultado = await ExcelAiService.anexarArquivos(id, pendentes);
        avisarRejeitados(resultado?.rejeitados);
        setPendentes([]);

        if (!resultado?.aceitos?.length) {
          Toast.error("Nenhum arquivo pôde ser enviado.");
          return;
        }
      }

      const resultado = await ExcelAiService.processar(id);

      if (resultado?.success) {
        Toast.success("Processamento iniciado. Acompanhe abaixo.");
      } else {
        // O caso comum aqui é processamento já em andamento. Não é erro do usuário: é informação.
        Toast.info(resultado?.message || "Não foi possível iniciar o processamento agora.");
      }
      setSinalAtualizacao((n) => n + 1);
    } catch (erro) {
      console.error("Erro ao processar arquivos:", erro);
      Toast.error("Falha ao iniciar o processamento.");
    } finally {
      setProcessando(false);
    }
  };

  return (
    <>
      {/* Este aviso existe porque a alternativa é o usuário perder dado sem entender por quê:
          os dois modos reconstroem a mesma partição do banco. */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 20 }}
        message="Cada processamento substitui a carga anterior desta empresa"
        description={
          <>
            Os dados gerados aqui ocupam o mesmo lugar dos que vêm pela planilha no modelo padrão.
            Processar por IA substitui o que foi carregado antes — e processar de novo pelo modelo
            padrão substitui o que a IA carregou. Use um modo de cada vez para a mesma empresa.
          </>
        }
      />

      <ExcelAiFilesCard
        syncId={syncId}
        podeEditar={podeEditar}
        processando={processando}
        onProcessar={processar}
        pendentes={pendentes}
        onPendentesChange={setPendentes}
        onRejeitados={avisarRejeitados}
      />

      {/* Sem empresa salva ainda não há processamento nenhum para listar. */}
      {syncId && (
        <ExcelAiRunsCard
          syncId={syncId}
          atualizarSinal={sinalAtualizacao}
          onAbrirDetalhe={setRunSelecionado}
          onRunFinalizado={onRunFinalizado}
        />
      )}

      {/* O detalhe continua em drawer: é vista de aprofundamento, não fluxo principal. */}
      <ExcelAiRunDetailDrawer
        aberto={!!runSelecionado}
        syncId={syncId}
        run={runSelecionado}
        podeEditar={podeEditar}
        onFechar={() => setRunSelecionado(null)}
        onReprocessado={() => setSinalAtualizacao((n) => n + 1)}
      />
    </>
  );
};

export default ExcelAiPanel;
